import { useTranslation } from 'next-i18next'

import { useEffect, useRef, useState } from 'react'
import { v4 as uuid } from 'uuid'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import Divider from '~/bootcamp/Activities/components/Divider'
import Equals from '~/bootcamp/Activities/components/Equals'
import Input from '~/bootcamp/Activities/components/Input'
import FractionsActivityWrapper from '~/bootcamp/Activities/Fractions/components/FractionsActivityWrapper'
import useFocus from '~/bootcamp/Activities/hooks/useFocus'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import { hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import { getRangesByDifficulty } from './constants'
import { generateTerms, QuestionValue } from './helpers'

export interface AnsValue {
  numerator?: number
  denominator?: number
}

function FractionSimplify({
  answerState,
  difficultyLevel,
  onNextQuestionClick,
  onAnswerStateChange,
  onInstanceChange,
  autofocus = true,
  actionButtonsFC,
}: ActivityProps): React.ReactElement {
  const { t } = useTranslation('generics')
  const [answer, setAnswer] = useState<AnsValue>({})
  const [questionAns, setQuestionAns] = useState<QuestionValue | undefined>(undefined)
  const [numerator, setNumerator] = useState<number>(1)
  const [denominator, setDenominator] = useState<number>(2)
  const [taskHistory, setTaskHistory] = useState<number[]>([])
  const [inputKey, setInputKey] = useState<string>(uuid())

  const { maxVariation } = getRangesByDifficulty(difficultyLevel)

  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  // The unreduced fraction only: `questionAns` IS the reduced form the student must find.
  useEmitInstance(
    onInstanceChange,
    denominator ? `\\frac{${numerator}}{${denominator}} = \\frac{\\square}{\\square}` : null,
  )

  useEffect(() => {
    setAnswer({})

    const { numerator: num, denominator: denom, ans } = generateTerms(difficultyLevel, taskHistory)

    setNumerator(num)
    setDenominator(denom)
    setQuestionAns(ans)
  }, [difficultyLevel, taskHistory])

  useEffect(() => {
    onAnswerStateChange(null)
  }, [answer, onAnswerStateChange])

  const handleOnCheckAnswerClick = (): void => {
    const ansResult =
      answer.numerator && answer.denominator
        ? Number(answer.numerator) === questionAns?.num && Number(answer.denominator) === questionAns?.denom
        : false

    onAnswerStateChange(ansResult)
  }

  const handleOnNextQuestionClick = (): void => {
    setTaskHistory((prevState) => {
      if (answer !== null) return [numerator, ...prevState].slice(0, maxVariation)
      else return [...prevState].slice(0, maxVariation)
    })
    onAnswerStateChange(null)
    onNextQuestionClick()
    setInputKey(uuid())
  }

  const onEnterPress = (): void => {
    if (answer.numerator !== undefined && answer.denominator !== undefined) {
      hocOnEnterPress(answerState, handleOnCheckAnswerClick, handleOnNextQuestionClick)
    }
  }

  return (
    <ActivityWrapper>
      <FractionsActivityWrapper>
        <div className="w-6 flex flex-col items-center">
          {numerator}
          <Divider />
          {denominator}
        </div>
        <Equals />
        <div className="w-6 flex flex-col items-center">
          <Input
            aria-label={t('ariaLabelButtons.insertNumerator')}
            autoFocus={autofocus}
            isError={answerState === false}
            key={`${inputKey}-first`}
            maxDigits={4}
            onChange={(value) =>
              setAnswer((prevState: AnsValue) => {
                return { ...prevState, numerator: Number(value) }
              })
            }
            onEnter={onEnterPress}
            ref={inputRef}
            value={`${answer.numerator || ''}`}
          />
          <Divider className="my-2" />
          <Input
            aria-label={t('ariaLabelButtons.insertDenominator')}
            isError={answerState === false}
            key={`${inputKey}-second`}
            maxDigits={4}
            onChange={(value) =>
              setAnswer((prevState: AnsValue) => {
                return { ...prevState, denominator: Number(value) }
              })
            }
            onEnter={onEnterPress}
            value={`${answer.denominator || ''}`}
          />
        </div>
      </FractionsActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid: answer.numerator !== undefined && answer.denominator !== undefined,
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleOnCheckAnswerClick,
          clearFormAction: () => {
            setAnswer({})
          },
          goToNextQuestionAction: handleOnNextQuestionClick,
        })}
    </ActivityWrapper>
  )
}

export default FractionSimplify
